import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Typography, 
  Paper,
  makeStyles,
  Divider,
  Grid,
  Avatar,
  Box,
  Button
} from '@material-ui/core';
import moment from 'moment';

// Icons
import ErrorIcon from '@material-ui/icons/RotateLeft';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HistoryIcon from '@material-ui/icons/History';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CommentIcon from '@material-ui/icons/Comment';
import PersonIcon from '@material-ui/icons/Person';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: '8px',
    marginBottom: '8px'
  },
  changeItem: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    position: 'relative',
    borderLeft: `2px solid ${theme.palette.primary.main}`
  },
  requested: {
    borderLeftColor: theme.palette.warning.main
  },
  withdrawn: {
    borderLeftColor: theme.palette.grey[500]
  },
  replied: {
    borderLeftColor: theme.palette.success.main
  },
  avatar: {
    marginRight: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3)
  },
  avatarRequested: {
    backgroundColor: theme.palette.warning.main,
  },
  avatarWithdrawn: {
    backgroundColor: theme.palette.grey[500],
  },
  avatarReplied: {
    backgroundColor: theme.palette.success.main,
  },
  comment: {
    whiteSpace: 'pre-line',
    marginTop: theme.spacing(0.5),
    fontSize: '0.8rem'
  },
  dateLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem'
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  requestTitle: {
    fontSize: '0.9rem',
    fontWeight: 500,
    marginBottom: theme.spacing(0.25)
  },
  requestInfo: {
    fontSize: '0.8rem'
  },
  commentLabel: {
    fontSize: '0.75rem'
  },
  icon: {
    fontSize: '1rem'
  },
  historyButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));

const ChangeRequestsTimeline = ({ changeRequests }) => {
  const classes = useStyles();
  const [showAllHistory, setShowAllHistory] = useState(false);
  
  if (!changeRequests || changeRequests.length === 0) {
    return null;
  }
  
  // Sort change requests by date, newest first
  const sortedRequests = [...changeRequests].sort((a, b) => 
    new Date(b.requestedAt) - new Date(a.requestedAt)
  );
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'requested':
        return <ErrorIcon className={classes.icon} />;
      case 'replied':
        return <CheckCircleIcon className={classes.icon} />;
      case 'withdrawn':
        return <CancelIcon className={classes.icon} />;
      default:
        return null;
    }
  };
  
  const getStatusClass = (status, classes) => {
    switch (status) {
      case 'requested':
        return {
          avatarClass: classes.avatarRequested,
          itemClass: classes.requested,
        };
      case 'replied':
        return {
          avatarClass: classes.avatarReplied,
          itemClass: classes.replied,
        };
      case 'withdrawn':
        return {
          avatarClass: classes.avatarWithdrawn,
          itemClass: classes.withdrawn,
        };
      default:
        return {
          avatarClass: '',
          itemClass: '',
        };
    }
  };
  
  const formatDate = (dateString) => {
    return moment(dateString).format('YYYY-MM-DD HH:mm');
  };

  // Determine which requests to show
  const visibleRequests = showAllHistory ? sortedRequests : [sortedRequests[0]];
  const hasMoreRequests = sortedRequests.length > 1;
  
  const toggleHistoryVisibility = () => {
    setShowAllHistory(!showAllHistory);
  };
  
  const renderChangeRequest = (request) => {
    const statusClasses = getStatusClass(request.status, classes);
    return (
      <React.Fragment key={request.id}>
        <Paper 
          variant="outlined"
          className={`${classes.changeItem} ${statusClasses.itemClass}`}
        >
          <Grid container alignItems="flex-start" wrap="nowrap">
            <Grid item>
              <Avatar className={`${classes.avatar} ${statusClasses.avatarClass}`}>
                {getStatusIcon(request.status)}
              </Avatar>
            </Grid>
            <Grid item xs>
              {/* 1. Change request title with state */}
              <Typography className={classes.requestTitle}>
                {request.status === 'requested' ? 'Żądanie poprawek' : 
                 request.status === 'withdrawn' ? 'Żądanie poprawek (wycofane)' : 
                 'Żądanie poprawek (odesłane do urzędu)'}
              </Typography>
              
              {/* Person who requested */}
              <Box display="flex" alignItems="center">
                <PersonIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                <Typography className={classes.requestInfo}>
                  Zgłaszający: {request.requestedBy.firstName} {request.requestedBy.lastName}
                </Typography>
              </Box>

              {/* 2. Change request date */}
              <Box display="flex" alignItems="center" mt={0.5}>
                <AccessTimeIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                <Typography className={classes.requestInfo}>
                  Data zgłoszenia: {formatDate(request.requestedAt)}
                </Typography>
              </Box>
              
              {/* 3. Change request deadline */}
              {request.deadline && (
                <Box display="flex" alignItems="center" mt={0.5}>
                  <AccessTimeIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                  <Typography className={classes.requestInfo}>
                    Termin na odesłanie zmian: {formatDate(request.deadline)}
                  </Typography>
                </Box>
              )}
              
              {/* 4. Change request response date */}
              {request.status === 'replied' && request.repliedAt && (
                <Box display="flex" alignItems="center" mt={0.5}>
                  <AccessTimeIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                  <Typography className={classes.requestInfo}>
                    Data nadesłania poprawek: {formatDate(request.repliedAt)}
                  </Typography>
                </Box>
              )}
              {/* 5. Change request withdrawal date */}
              {request.status === 'withdrawn' && request.withdrawnAt && (
                <Box display="flex" alignItems="center" mt={0.5}>
                  <AccessTimeIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                  <Typography className={classes.requestInfo}>
                    Data wycofania: {formatDate(request.withdrawnAt)}
                  </Typography>
                </Box>
              )}
              
              {/* Request comment */}
              <Box mt={0.5} mb={0.5}>
                <Box display="flex" alignItems="center">
                  <CommentIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                  <Typography variant="body2" className={classes.commentLabel}>
                    Komentarz:
                  </Typography>
                </Box>
                <Typography className={classes.comment}>
                  {request.requestComment}
                </Typography>
              </Box>


              {request.status === 'withdrawn' && <Divider className={classes.divider} />}
              {/* Person who withdrew */}
              {request.status === 'withdrawn' && request.withdrawnBy && (
                <Box display="flex" alignItems="center">
                  <PersonIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                  <Typography className={classes.requestInfo}>
                    Wycofane przez: {request.withdrawnBy.firstName} {request.withdrawnBy.lastName}
                  </Typography>
                </Box>
              )}
              
              {/* Withdrawal comment */}
              {request.status === 'withdrawn' && request.withdrawalComment && (
                <Box mt={1}>
                  <Box display="flex" alignItems="center">
                    <CommentIcon fontSize="small" style={{ marginRight: 4, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                    <Typography variant="body2" className={classes.commentLabel}>
                      Komentarz do wycofania:
                    </Typography>
                  </Box>
                  <Typography className={classes.comment}>
                    {request.withdrawalComment}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </React.Fragment>
    );
  };
  
  return (
    <React.Fragment>
      <Typography variant="subtitle2" color="textPrimary" gutterBottom style={{ fontSize: '1rem', marginBottom: '8px' }}>
        Uzupełnienia i poprawki
      </Typography>
      
      {visibleRequests.map(request => renderChangeRequest(request))}
      
      {hasMoreRequests && (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<HistoryIcon />}
          className={classes.historyButton}
          onClick={toggleHistoryVisibility}
        >
          {showAllHistory ? 'Ukryj historię' : 'Historia żądań'}
        </Button>
      )}
    </React.Fragment>
  );
};

ChangeRequestsTimeline.propTypes = {
  changeRequests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      requestComment: PropTypes.string.isRequired,
      requestedAt: PropTypes.string.isRequired,
      requestedBy: PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }).isRequired,
      deadline: PropTypes.string,
      withdrawalComment: PropTypes.string,
      withdrawnAt: PropTypes.string,
      withdrawnBy: PropTypes.shape({
        id: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
      }),
      status: PropTypes.string.isRequired,
      repliedAt: PropTypes.string,
      replyComment: PropTypes.string,
    })
  )
};

export default ChangeRequestsTimeline;


// WEBPACK FOOTER //
// ./src/components/budget/ChangeRequestsTimeline.js